import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';

// Keyframe animations
const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(1.05);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const checkmarkDraw = keyframes`
  0% {
    stroke-dashoffset: 100;
  }
  100% {
    stroke-dashoffset: 0;
  }
`;

const LoadingSpinner = ({ message = 'Loading...', variant = 'default' }) => {
    if (variant === 'dots') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    animation: `${fadeIn} 0.5s ease-in`,
                }}
            >
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    {[0, 1, 2].map((index) => (
                        <Box
                            key={index}
                            sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                animation: `${bounce} 1.4s infinite ease-in-out both`,
                                animationDelay: `${index * 0.16}s`,
                            }}
                        />
                    ))}
                </Box>
                <Typography
                    variant="h6"
                    sx={{
                        color: '#1e293b',
                        fontWeight: 600,
                        animation: `${pulse} 2s ease-in-out infinite`,
                    }}
                >
                    {message}
                </Typography>
            </Box>
        );
    }

    if (variant === 'circular') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    animation: `${fadeIn} 0.5s ease-in`,
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: 100,
                        height: 100,
                        mb: 3,
                    }}
                >
                    {/* Outer ring */}
                    <Box
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            border: '4px solid rgba(239, 68, 68, 0.2)',
                            borderTop: '4px solid #ef4444',
                            borderRadius: '50%',
                            animation: `${spin} 1s linear infinite`,
                        }}
                    />
                    {/* Inner ring */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '20%',
                            left: '20%',
                            width: '60%',
                            height: '60%',
                            border: '4px solid rgba(59, 130, 246, 0.2)',
                            borderBottom: '4px solid #3b82f6',
                            borderRadius: '50%',
                            animation: `${spin} 0.75s linear infinite reverse`,
                        }}
                    />
                </Box>
                <Typography
                    variant="h6"
                    sx={{
                        color: '#1e293b',
                        fontWeight: 600,
                        animation: `${pulse} 2s ease-in-out infinite`,
                    }}
                >
                    {message}
                </Typography>
            </Box>
        );
    }

    // Default variant - modern clean spinner
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                bgcolor: '#ffffff',
                animation: `${fadeIn} 0.5s ease-in`,
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: 60,
                    height: 60,
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        border: '3px solid rgba(239, 68, 68, 0.1)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        border: '3px solid transparent',
                        borderTopColor: '#ef4444',
                        animation: `${spin} 1s linear infinite`,
                    }}
                />
            </Box>
        </Box>
    );
};

export default LoadingSpinner;
